import { useQueryClient } from '@tanstack/react-query';

import { useCreateContentRegisterFileGenerationProcessMutation } from 'components/hooks/contentRegisterFileGenerationProcessMutations';
import { useContentRegisterFileGenerationProcessQuery } from 'components/hooks/contentRegisterFileGenerationProcessQueries';
import useModal from 'components/hooks/useModal';

const calculateDownloadState = ({ hasErrored, status, url }) => {
  if (hasErrored || status === 'failed') return 'failed';
  if (status === 'completed' && url) return 'succeeded';

  return 'inProgress';
};

export default function useRiskAssessmentsPdfDownload() {
  const queryClient = useQueryClient();

  const [isModalOpen, , openModal, closeModal] = useModal(false);

  const createMutation =
    useCreateContentRegisterFileGenerationProcessMutation();
  const fileGenerationProcessId = createMutation.data?.data?.id;

  const { data: fileGenerationProcess, isError: isPollingError } =
    useContentRegisterFileGenerationProcessQuery({ fileGenerationProcessId });

  const { status, url } = fileGenerationProcess?.data?.attributes || {};

  const downloadState = calculateDownloadState({
    hasErrored: createMutation.isError || isPollingError,
    status,
    url,
  });

  const downloadStatus = {
    downloadFailed: downloadState === 'failed',
    downloadInProgress: downloadState === 'inProgress',
    downloadLink: url,
    downloadSucceeded: downloadState === 'succeeded',
  };

  const forgetProcess = (id) => {
    if (id) {
      queryClient.removeQueries({
        queryKey: ['contentRegister', 'fileGenerationProcesses', id],
      });
    }
  };

  const startDownload = (variables) => {
    forgetProcess(fileGenerationProcessId);
    createMutation.reset();
    createMutation.mutate(variables);
  };

  const requestDownload = (masterActivityIds) => {
    openModal();
    startDownload({ masterActivityIds });
  };

  return {
    closeModal,
    downloadAction: () => window.open(url, '_blank'),
    downloadStatus,
    isModalOpen,
    requestDownload,
    resetDownload: () => {
      forgetProcess(fileGenerationProcessId);
      createMutation.reset();
    },
    retryDownload: () => startDownload(createMutation.variables),
  };
}
